import { useState } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { SubcontractorUser } from '../types';
import UserModal from '../../settings/components/subcontractors/UserModal';

const CREATE_SENTINEL = '__create__';

interface BookingContactUserSelectProps {
  label: string;
  addUserOptionLabel: string;
  noUsersLabel: string;
  subcontractorId: string;
  users: SubcontractorUser[];
  value: string;
  onChange: (id: string) => void;
  onAddUser: (
    subcontractorId: string,
    firstName: string,
    lastName: string,
  ) => Promise<SubcontractorUser | null>;
  error?: string;
  disabled?: boolean;
}

export default function BookingContactUserSelect({
  label,
  addUserOptionLabel,
  noUsersLabel,
  subcontractorId,
  users,
  value,
  onChange,
  onAddUser,
  error,
  disabled,
}: BookingContactUserSelectProps) {
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  return (
    <>
      <FormControl fullWidth required error={!!error} disabled={disabled || !subcontractorId}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={value}
          onChange={(e) => {
            if (e.target.value === CREATE_SENTINEL) {
              setQuickCreateOpen(true);
              return;
            }
            onChange(e.target.value);
          }}
          inputProps={{ 'aria-label': label }}
        >
          {subcontractorId && (
            <MenuItem value={CREATE_SENTINEL} data-testid="option-add-user" className="text-primary">
              <AddIcon fontSize="small" className="mr-1" />
              {addUserOptionLabel}
            </MenuItem>
          )}
          {users.length === 0 && subcontractorId ? (
            <MenuItem disabled value="">
              {noUsersLabel}
            </MenuItem>
          ) : (
            users.map((u) => (
              <MenuItem key={u.id} value={u.id} data-testid={`option-${u.id}`}>
                {u.first_name} {u.last_name}
              </MenuItem>
            ))
          )}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
      {quickCreateOpen && subcontractorId && (
        <UserModal
          onClose={() => setQuickCreateOpen(false)}
          onSave={async (firstName, lastName) => {
            const created = await onAddUser(subcontractorId, firstName, lastName);
            if (!created) return false;
            onChange(created.id);
            setQuickCreateOpen(false);
            return true;
          }}
        />
      )}
    </>
  );
}
